import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Plus, Minus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, UserSearch, ChartLine } from "lucide-react";

/* ======= Assets ======= */
import HeroBG from "@/assets/Frame.png";
import DashboardImage from "@/assets/Dashboard.svg";
import CandidateImage from "@/assets/Candidates.svg";
import ClientImage from "@/assets/Clients.svg";
import SkillsImage from "@/assets/Consolidated_Report.svg";
import { motion, Variants } from "framer-motion";

const GradientIcon = ({ icon: Icon }) => {
  const gradientId = "icon-gradient";

  return (
    <svg width="0" height="0">
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="hsl(var(--primary))" />
          <stop offset="100%" stopColor="hsl(var(--secondary))" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const slideInLeft: Variants = {
  hidden: { opacity: 0, x: -100 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 1.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const slideInRight: Variants = {
  hidden: { opacity: 0, x: 100 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 1.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const slideInTop: Variants = {
  hidden: { opacity: 0, y: -80 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 1.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const testimonialContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.18,
      delayChildren: 0.2,
    },
  },
};

const testimonialItem: Variants = {
  hidden: { opacity: 0, y: 50, rotateX: 8, filter: "blur(4px)" },
  visible: {
    opacity: 1,
    y: 0,
    rotateX: 0,
    filter: "blur(0px)",
    transition: { duration: 1.0, ease: [0.16, 1, 0.3, 1] },
  },
};

const ModernHiring = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question:
        "Can Smartborn manage candidates, jobs, and vendors in one place?",
      answer:
        "Smartborn brings candidate tracking, job management, vendor coordination, and hiring pipelines together in a single integrated platform.",
    },
    {
      question:
        "Does Smartborn support interview scheduling and activity tracking?",
      answer:
        "Yes, Smartborn enables seamless interview scheduling and centralized activity tracking. Recruiters can manage follow-ups, feedback, and candidate interactions in one organized system.",
    },
    {
      question:
        "How does Smartborn provide recruitment analytics and reporting?",
      answer:
        "Smartborn offers real-time dashboards and detailed recruitment reports. Teams can track performance metrics and make informed hiring decisions with actionable insights.",
    },
    {
      question: "Is Smartborn suitable for enterprises and staffing firms?",
      answer:
        "Yes, Smartborn is built to support both growing teams and large recruitment operations. Its scalable workflows and performance tracking make it ideal for staffing firms and enterprises.",
    },
  ];

  return (
    <div className="bg-background">
      <Navigation />

      {/* ================= HERO ================= */}
      <section
        className="relative pt-28 pb-24 overflow-hidden"
        style={{
          backgroundImage: `url(${HeroBG})`,
          backgroundSize: "cover",
          backgroundPosition: "left",
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="container relative mx-auto px-6 max-w-3xl text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="bg-duo-gradient bg-clip-text text-transparent">The Platform for</span>
            <br />
            <span className="bg-duo-gradient bg-clip-text text-transparent">Modern Hiring</span>
          </h1>

          <p className="text-white sm:text-lg lg:text-xl mb-8 max-w-2xl mx-auto">
            Smartborn helps teams manage hiring, track candidates and
            vendors, and analyze recruitment performance on one
            powerful recruitment platform.
          </p>

          <Button size="lg" asChild className="bg-primary hover:bg-primary/90 text-white border-0">
            <Link to="/contact">
              Request Demo
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>

      {/* ================= DASHBOARD SECTION ================= */}
      <section className="py-28 bg-gradient-to-b from-primary/10 to-secondary/10">
        <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          {/* TEXT */}
          <div className="max-w-xl">
            <motion.div
              className="text-center lg:text-left"
              variants={slideInLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.4 }}
            >
              <p className="text-lg font-medium text-primary mb-3">
                Recruitment Dashboard
              </p>

              <h2 className="text-4xl font-bold mb-6 leading-tight text-foreground">
                See your hiring <br /> at a glance.
              </h2>

              <p className="text-muted-foreground text-lg leading-relaxed">
                Track jobs, candidates, submissions, interviews, and closures
                from a single real-time dashboard. Gain instant visibility into
                hiring performance to make faster, data-driven decisions.
              </p>
            </motion.div>
          </div>

          {/* IMAGE */}
          <div className="relative">
            <motion.div
              className="text-center lg:text-left"
              variants={slideInRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.4 }}
            >
              <img
                src={DashboardImage}
                alt="Dashboard"
                className="rounded-2xl w-full shadow-elegant"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ================= SOLUTIONS ================= */}
      <section className="py-20 bg-muted/30 text-center">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-foreground">
            Smart hiring solutions for every team
          </h2>

          <motion.div
            className="grid md:grid-cols-3 gap-8"
            variants={testimonialContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.3 }}
          >
            {[
              {
                icon: ClipboardList,
                title: "For Staffing Agencies",
                desc: "Deliver faster, better-quality placements and accelerate client hiring outcomes.",
              },
              {
                icon: UserSearch,
                title: "For Corporate HR Teams",
                desc: "HR digital toolkit, more efficiency and strengthen internal hiring performance.",
              },
              {
                icon: ChartLine,
                title: "For Growing Companies",
                desc: "Build stronger teams with better tracking and collaboration tools.",
              },
            ].map((item, i) => {
              const Icon = item.icon;

              return (
                <motion.div
                  key={i}
                  variants={testimonialItem}
                  className={`${i % 2 === 0 ? 'bg-primary/5 border-primary/20' : 'bg-secondary/5 border-secondary/20'} border-2 rounded-xl p-8 shadow-sm text-left hover:shadow-elegant transition-all duration-300`}
                >
                  <div className="mb-4 relative">
                    <svg width="0" height="0">
                      <defs>
                        <linearGradient
                          id={`cardIconGradient-${i}`}
                          x1="0%"
                          y1="0%"
                          x2="100%"
                          y2="100%"
                        >
                          <stop offset="0%" stopColor="hsl(var(--primary))" />
                          <stop offset="100%" stopColor="hsl(var(--secondary))" />
                        </linearGradient>
                      </defs>
                    </svg>

                    <Icon
                      className={`w-10 h-10 ${i % 2 === 0 ? 'shadow-glow' : 'shadow-glow-secondary'}`}
                      stroke={`url(#cardIconGradient-${i})`}
                      strokeWidth={2}
                    />
                  </div>

                  <h4 className="text-xl font-bold mb-3 text-foreground">{item.title}</h4>
                  <p className="text-muted-foreground">{item.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ================= STATS ================= */}
      <section className="py-20 bg-duo-gradient text-center text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent" />
        <div className="container relative z-10 mx-auto px-6">
          <h3 className="text-3xl md:text-4xl font-bold mb-16">
            Trusted by teams managing modern workforces
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: "10+", label: "companies" },
              { number: "120+", label: "employees" },
              { number: "250+", label: "candidates" },
              { number: "99%", label: "satisfaction" },
            ].map((item, i) => (
              <motion.div
                key={i}
                variants={slideInTop}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.4 }}
                className="text-center"
              >
                <div className={`text-4xl md:text-5xl font-bold mb-2 ${i % 2 === 0 ? 'text-white' : 'text-white/90'}`}>
                  {item.number}
                </div>
                <div className="text-lg opacity-80 uppercase tracking-widest text-sm font-semibold">
                  {item.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FEATURE SECTIONS ================= */}
      <section className="py-28 bg-gradient-to-b from-background to-primary/5">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* TEXT */}
            <div className="max-w-lg">
              <motion.div
                className="text-center lg:text-left"
                variants={slideInRight}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.4 }}
              >
                <p className="text-lg font-medium text-primary mb-4">
                  Candidate Hub
                </p>

                <h3 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight text-foreground">
                  Track candidates easily.
                </h3>

                <p className="text-muted-foreground text-lg leading-relaxed">
                  Search, filter, and manage candidates in one organized
                  workspace. Review profiles, matches, and progress to move the
                  right people forward.
                </p>
              </motion.div>
            </div>

            {/* IMAGE */}
            <div className="flex justify-center lg:justify-end">
              <motion.div
                className="text-center lg:text-left"
                variants={slideInLeft}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.4 }}
              >
                <img
                  src={CandidateImage}
                  alt="Candidates"
                  className="w-full max-w-2xl rounded-2xl shadow-elegant"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Management */}
      <section className="py-28 bg-gradient-to-b from-primary/5 to-secondary/5">
        <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-20 items-center">
          {/* IMAGE LEFT */}
          <div className="order-2 lg:order-1">
            <motion.div
              className="text-center lg:text-left"
              variants={slideInLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.4 }}
            >
              <img
                src={ClientImage}
                alt="Clients"
                className="rounded-2xl w-full shadow-elegant"
              />
            </motion.div>
          </div>

          {/* TEXT RIGHT */}
          <div className="max-w-xl order-1 lg:order-2">
            <motion.div
              className="text-center lg:text-left"
              variants={slideInRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.4 }}
            >
              <p className="text-lg font-medium text-secondary mb-3">
                Client Management
              </p>

              <h3 className="text-4xl font-bold mb-6 text-foreground">
                Organize hiring partnerships.
              </h3>

              <p className="text-muted-foreground text-lg leading-relaxed">
                Centralize client information and ongoing recruitment activity
                in a single workspace. Monitor job pipelines and hiring status
                as they progress through each stage.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Skills Intelligence */}
      <section className="py-28 bg-gradient-to-b from-secondary/5 to-background">
        <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-20 items-center">
          <div className="max-w-xl">
            <motion.div
              className="text-center lg:text-left"
              variants={slideInRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.4 }}
            >
              <p className="text-lg font-medium text-primary mb-3">
                Skills Intelligence
              </p>

              <h3 className="text-4xl font-bold mb-6 text-foreground">
                Assess workforce skill demands.
              </h3>

              <p className="text-muted-foreground text-lg leading-relaxed">
                Analyze how skills are distributed, where gaps exist, and which
                skills lead to hires. Use visual insights to align hiring
                strategy with real market demand.
              </p>
            </motion.div>
          </div>

          <div>
            <motion.div
              className="text-center lg:text-left"
              variants={slideInLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.4 }}
            >
              <img
                src={SkillsImage}
                alt="Skills"
                className="rounded-2xl w-full shadow-elegant"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ================= FAQ ================= */}
      <section className="py-28 bg-muted/30">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 className="text-4xl font-bold mb-12 text-center text-foreground">
            Frequently Asked Questions
          </h2>

          <div className="bg-background rounded-2xl overflow-hidden shadow-card border border-border/50">
            {faqs.map((item, index) => {
              const isOpen = openIndex === index;
              const isLast = index === faqs.length - 1;

              return (
                <div
                  key={index}
                  className={`
                    cursor-pointer transition-all duration-300
                    ${!isLast ? "border-b border-border/50" : ""}
                    ${isOpen ? "bg-primary/5" : "bg-background hover:bg-muted/50"}
                  `}
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                >
                  <div className="p-6 flex justify-between items-center">
                    <span className={`font-semibold text-lg ${isOpen ? 'text-primary' : 'text-foreground'}`}>
                      {item.question}
                    </span>
                    {isOpen ? <Minus className="text-primary" /> : <Plus className="text-muted-foreground" />}
                  </div>

                  <div
                    className={`
                      grid transition-all duration-500 ease-in-out
                      ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}
                    `}
                  >
                    <div className="overflow-hidden">
                      <div className="px-6 pb-6">
                        <p className="text-muted-foreground leading-relaxed">
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ModernHiring;
